import React from "react";
import PropTypes from "prop-types";

/**
 * Composant d'en-tête réutilisable pour les pages
 * 
 * @param {Object} props - Les propriétés du composant
 * @param {string} props.title - Le titre principal de la page
 * @param {string} [props.description] - La description optionnelle sous le titre
 * @param {React.ReactNode} [props.actions] - Les actions à afficher dans l'en-tête (boutons, etc.)
 * @param {string} [props.accentColor="#57A863"] - La couleur d'accent pour le séparateur
 * @param {string} [props.className] - Classes CSS supplémentaires
 * @param {boolean} [props.showDivider=true] - Afficher ou masquer la ligne séparatrice colorée
 * @param {string} [props.size="default"] - Taille de l'en-tête ('small', 'default', 'large')
 */
const HeaderSection = ({ 
  title, 
  description,
  actions,
  accentColor = "#57A863",
  className = "",
  showDivider = true,
  size = "default"
}) => {
  // Configuration de taille
  const sizeClasses = {
    small: {
      container: "mb-4",
      title: "text-xl font-semibold",
      description: "mt-1 text-sm",
      divider: "h-0.5 w-12"
    },
    default: {
      container: "mb-8",
      title: "text-3xl font-bold",
      description: "mt-2 text-base",
      divider: "h-1 w-16"
    },
    large: {
      container: "mb-10",
      title: "text-4xl font-bold",
      description: "mt-3 text-lg",
      divider: "h-1.5 w-20"
    }
  };

  const currentSize = sizeClasses[size] || sizeClasses.default;

  return (
    <div className={`${currentSize.container} ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className={`${currentSize.title} text-gray-800`}>
            {title}
          </h1>
          {description && (
            <p className={`${currentSize.description} text-gray-600`}>
              {description}
            </p>
          )}
        </div>

        {actions && (
          <div className="flex items-center gap-3">
            {actions}
          </div>
        )}
      </div>

      {showDivider && (
        <div 
          className={`${currentSize.divider} mt-4 rounded-full`} 
          style={{ backgroundColor: accentColor }}
        ></div>
      )}
    </div>
  );
};

HeaderSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  actions: PropTypes.node,
  accentColor: PropTypes.string,
  className: PropTypes.string,
  showDivider: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'default', 'large'])
};

export default HeaderSection;